import React, { useState, useEffect, useRef } from 'react';
import { useAppData } from './context/DataContext';
import { Alert, Modal, ConfigProvider } from 'antd';
import axios from 'axios';
import useRedirect from './useRedirect';
import ForgotPassword from "./ForgotPassword.js";
import { useNavigate } from 'react-router-dom';

const secrets = require('./secrets.js');
const api = secrets.server_url;

async function createOrganizer(name, email, password, setError) {
    try {
        // First create a user with organizer capabilities
        const userData = {
            name: name,
            email: email,
            password: password,
            notificationPrefs: {
                email: true,
                text: true
            }
        };
        
        const response = await axios.post(`${api}users/`, userData, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        });
        
        if (response.data.message === 'Email already exists') {
            setError('This email already exists.');
            return false;
        }
        
        // The backend should automatically create a default organization
        // Store the user data and organization data
        if (response.data.data && response.data.data.user) {
            localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));
            if (response.data.data.organization) {
                localStorage.setItem('defaultOrganization', JSON.stringify(response.data.data.organization));
            }
            return response.data;
        }
    } catch (error) {
        console.error('Error creating organizer:', error);
        setError('An error occurred. Please try again.');
        throw error;
    }
}

async function addOrganizationToUser(organizationId, userEmail) {
    try {
        // Get the user ID first
        const userResponse = await axios.get(`${api}users/${userEmail}`, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
            }
        });
        
        if (!userResponse.data.data) {
            console.error('User not found');
            return false;
        }
        
        const userId = userResponse.data.data._id;
        
        // Update the user to add the organization to their list
        const response = await axios.put(`${api}user/${userId}`, {
            organizations: [...(userResponse.data.data.organizations || []), organizationId]
        }, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error('Error adding organization to user:', error);
        throw error;
    }
}

async function handleSignUp(name, newPassword, email, setError) {
    try {
        // Create user with organizer capabilities (includes default organization)
        const userResponse = await createOrganizer(name, email, newPassword, setError);
        
        if (!userResponse || !userResponse.data || !userResponse.data.user) {
            return; // Error already handled in createOrganizer
        }
        
        // The user is created with a default organization, no need to create another one
        // unless we want a specifically named one
        if (name !== `${userResponse.data.user.name}'s Organization`) {
            // Create an additional named organization if needed
            const orgResponse = await createOrganization(name, email, setError);
            
            if (orgResponse && orgResponse.data && orgResponse.data._id) {
                // Add this organization to the user's organizations
                await addOrganizationToUser(orgResponse.data._id, email);
            }
        }
        
        // Redirect to organizer home page
        window.location.href = "/organizerHome";
    } catch (error) {
        console.error('Error during signup:', error);
        setError('An error occurred during signup. Please try again.');
    }
}

async function createOrganization(name, ownerEmail, setError) {
    try {
        // Get the user ID first
        const userResponse = await axios.get(`${api}users/${ownerEmail}`, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
            }
        });
        
        if (!userResponse.data.data) {
            setError('User not found. Please try again.');
            return false;
        }
        
        const userId = userResponse.data.data._id;
        
        // Create the organization with user ID as owner
        const data = {
            name: name,
            owner: userId
        };
        
        const response = await axios.post(`${api}organizations/`, data, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        });
        
        return response.data;
    } catch (error) {
        console.error('Error creating organization:', error);
        setError('Failed to create organization. Please try again.');
        throw error;
    }
}

function OrganizerSignIn(props) {
    const [isRegistered, setIsRegistered] = useState(true);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [liPassword, setLiPassword] = useState('');
    const [name, setName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMatchError, setPasswordMatchError] = useState('');
    const [emailError, setEmailError] = useState('');
    const orgNameInputRef = useRef(null);
    const { redirectToOrganizerHome } = useRedirect();
    const [emails, setEmails] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const [forgotPassword, setForgotPassword] = useState(false);
    const navigate = useNavigate();

    const { users } = useAppData();

    // Store the redirect URL in session storage when the component mounts
    useEffect(() => {
        if (props.redirectRoute) {
            sessionStorage.setItem('authRedirect', props.redirectRoute);
        }
    }, [props.redirectRoute]);

    // Handle redirection after successful authentication
    const handleRedirectAfterAuth = () => {
        // Check if we have a redirect route stored
        const redirectRoute = sessionStorage.getItem('authRedirect');
        if (redirectRoute) {
            // Clear the stored redirect
            sessionStorage.removeItem('authRedirect');
            // Redirect the user to the specified route
            navigate(redirectRoute);
        } else {
            // Default behavior depends on the context
            if (window.location.pathname.includes('/event/')) {
                // Stay on the same page if on an event page
                window.location.reload();
            } else {
                // Redirect to organizer home if not on event page
                redirectToOrganizerHome();
            }
        }
    };

    useEffect(() => {
        // Filter users who are part of organizations (organizers)
        const organizerEmails = users
            .filter(user => user.organizations && user.organizations.length > 0)
            .map(user => user.email)
            .filter(Boolean);
        setEmails(organizerEmails);
    }, [users]);

    async function handleSignin(email, password, setError, props) {
        try {
            const data = { password: password };
            const response = await axios.post(`${api}users/${email}`, data, {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.message === 'Authentication successful') {
                // Store user and organization data
                if (response.data.data && response.data.data.user) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));
                    if (response.data.data.organization) {
                        localStorage.setItem('defaultOrganization', JSON.stringify(response.data.data.organization));
                    }
                    
                    // Handle redirection based on context
                    handleRedirectAfterAuth();
                }
            } else {
                setError('Authentication failed. Please check your credentials.');
            }
        } catch (error) {
            console.error('Error:', error);
            if (error.response && error.response.data.message === 'Invalid password') {
                setError('Incorrect password');
            } else if (error.response && error.response.data.message === 'User not found') {
                setError('No user found with this email');
            } else {
                setError('An error occurred. Please try again.');
            }
        }
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (!email) {
            setIsRegistered(true);
        }
    };

    const handleEmailBlur = () => {
        if (email.trim()) {  // Only check if email is not empty
            if (!emails.includes(email)) {
                setIsRegistered(false);
                // Focus on orgNameInputRef if it's not null
                if (orgNameInputRef.current) {
                    orgNameInputRef.current.focus();
                }
            } else {
                setIsRegistered(true);
            }
        }
    };

    const handleClose = () => {
        props.setTrigger(false);
        setIsRegistered(true);
        setEmail('');
        setLiPassword('');
        setName('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setPasswordMatchError('');
        setEmailError('');
      };
      

    const handleSubmit = (event) => {
        event.preventDefault();

        if (isRegistered) {
            handleSignin(email, liPassword, setError);
        } else {
            if (newPassword !== confirmPassword) {
                setPasswordMatchError('Passwords do not match');
                return;
            }
            setPasswordMatchError('');
            setEmailError('');
            handleSignUp(name, newPassword, email, setEmailError);
        }
    };

    return (
        <>
            <ForgotPassword userType="User" trigger={forgotPassword} setTrigger={setForgotPassword} />
            <ConfigProvider
                theme={{
                    components: {
                        Modal: {
                            // Add any modal-specific styling here
                        },
                    },
                }}
            >
                <Modal
                    open={props.trigger}
                    onCancel={handleClose}
                    afterClose={handleClose}  // Add this line
                    footer={null}
                    className="signin-modal"
                    centered
                    title={
                        <div className="sign-title">
                            {!isRegistered ? (
                                <p>Organizer Sign Up</p>
                            ) : (
                                <p>Organizer Sign In or Sign Up</p>
                            )}
                        </div>
                    }
                    
                >

                    <div className="sign-in-text">
                        <p style={{ textAlign: 'left', marginBottom: '20px', color: '#444444' }}>
                            Welcome to Ummahfy! A org account allows you to create paid events, add team members, and more!
                        </p>
                    </div>


                    <form onSubmit={handleSubmit}>
                        <div className="inputs">
                            <label htmlFor="email">Email</label>
                            <input
                                required
                                type="email"
                                id="email"
                                name="email"
                                className="long-input"
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                                value={email}
                            />
                        </div>

                        {isRegistered ? (
                            <>
                                <div className="inputs">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        className="long-input"
                                        onChange={(e) => setLiPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <a className="forgot-password"
                                    onClick={() => {
                                        setForgotPassword(true)
                                        props.setTrigger(false)
                                    }}
                                >
                                    Forgot Password?
                                </a>

                                {error && (
                                    <Alert
                                        message={error}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setError('')}
                                    />
                                )}

                                <button type="submit" className="signin org-signin-btn">
                                    Organizer Sign In
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="inputs">
                                    <label htmlFor="organizer">Organization Name</label>
                                    <input
                                        type="text"
                                        id="org-name"
                                        name="org-name"
                                        className="long-input"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        ref={orgNameInputRef}
                                    />
                                </div>

                                <div className="inputs">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        className="long-input"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="inputs">
                                    <label htmlFor="confirm">Confirm Password</label>
                                    <input
                                        type="password"
                                        id="confirm"
                                        name="confirm"
                                        className="long-input"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                {passwordMatchError && (
                                    <Alert
                                        message={passwordMatchError}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setPasswordMatchError('')}
                                    />
                                )}

                                {emailError && (
                                    <Alert
                                        message={emailError}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setEmailError('')}
                                    />
                                )}

                                <button type="submit" className="signin org-signin-btn">
                                    Create Account
                                </button>
                            </>
                        )}
                    </form>
                </Modal>
            </ConfigProvider>
        </>
    );
}

export default OrganizerSignIn;